import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { 
  X, CalendarCheck, User, Phone, Mail, MapPin, Wrench, 
  Home, Clock, DollarSign, Camera, CheckCircle, Loader2 
} from 'lucide-react';
import { toast } from 'sonner';

const serviceTypes = [
  { value: 'appliance-sales', label: 'Refurbished Appliance Sales' },
  { value: 'plumbing', label: 'Plumbing' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'appliance', label: 'Appliance Repair' },
  { value: 'handyman', label: 'Handyman Services' },
  { value: 'other', label: 'Other' },
];

const propertyTypes = [
  { value: 'house', label: 'Single Family Home' },
  { value: 'condo', label: 'Condo / Townhouse' },
  { value: 'apartment', label: 'Apartment' },
  { value: 'commercial', label: 'Commercial Property' },
];

const urgencyLevels = [
  { value: 'emergency', label: 'Emergency (ASAP)' },
  { value: 'same-day', label: 'Same Day' },
  { value: 'this-week', label: 'This Week' },
  { value: 'flexible', label: 'Flexible' },
];

const budgetRanges = [
  { value: 'under-100', label: 'Under $100' },
  { value: '100-250', label: '$100 - $250' },
  { value: '250-500', label: '$250 - $500' },
  { value: '500-1000', label: '$500 - $1,000' },
  { value: 'over-1000', label: 'Over $1,000' },
  { value: 'not-sure', label: 'Not Sure' },
];

const timePreferences = [
  { value: 'morning', label: 'Morning (8am - 12pm)' },
  { value: 'afternoon', label: 'Afternoon (12pm - 5pm)' },
  { value: 'evening', label: 'Evening (5pm - 8pm)' },
  { value: 'anytime', label: 'Anytime' },
];

export const BookingForm = ({ isOpen, onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    // Contact Info
    name: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    zip: '',
    // Service Details
    serviceType: '',
    propertyType: '',
    urgency: '',
    // Project Details
    budget: '',
    description: '',
    // Scheduling
    preferredDate: '',
    preferredTime: '',
  });
  
  const addressInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  
  // Initialize Google Places Autocomplete
  useEffect(() => {
    if (!isOpen) return;
    
    // Wait for Google Maps API to load
    const initAutocomplete = () => {
      if (window.google && window.google.maps && window.google.maps.places && addressInputRef.current) {
        // Create autocomplete instance restricted to Ohio addresses
        autocompleteRef.current = new window.google.maps.places.Autocomplete(addressInputRef.current, {
          componentRestrictions: { country: 'us' },
          fields: ['address_components', 'formatted_address'],
          types: ['address']
        });
        
        // Listen for place selection
        autocompleteRef.current.addListener('place_changed', () => {
          const place = autocompleteRef.current.getPlace();
          
          if (place && place.address_components) {
            let streetNumber = '';
            let streetName = '';
            let city = '';
            let zip = '';
            
            place.address_components.forEach(component => {
              const types = component.types;
              
              if (types.includes('street_number')) {
                streetNumber = component.long_name;
              }
              if (types.includes('route')) {
                streetName = component.long_name;
              }
              if (types.includes('locality')) {
                city = component.long_name;
              }
              if (types.includes('postal_code')) {
                zip = component.long_name;
              }
            });
            
            const fullAddress = streetNumber ? `${streetNumber} ${streetName}` : streetName;
            
            setFormData(prev => ({
              ...prev,
              address: fullAddress,
              city: city,
              zip: zip
            }));
          }
        });
      }
    };
    
    // Check if Google API is already loaded
    if (window.google && window.google.maps && window.google.maps.places) {
      setTimeout(initAutocomplete, 100);
    } else {
      // Wait for script to load
      const checkInterval = setInterval(() => {
        if (window.google && window.google.maps && window.google.maps.places) {
          clearInterval(checkInterval);
          initAutocomplete();
        }
      }, 200);
      
      // Clean up interval after 10 seconds
      setTimeout(() => clearInterval(checkInterval), 10000);
      
      return () => clearInterval(checkInterval);
    }
  }, [isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    if (!formData.name || !formData.phone || !formData.email) {
      toast.error('Please fill in all contact information');
      return false;
    }
    if (!formData.serviceType) {
      toast.error('Please select a service type');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Google Ads conversion tracking
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'conversion', {'send_to': 'AW-17881958235/PI-fCLWyn_AbENuO5M5C'});
        }
        toast.success('Booking request submitted! We\'ll contact you shortly.');
        setFormData({
          name: '', phone: '', email: '', address: '', city: '', zip: '',
          serviceType: '', propertyType: '', urgency: '',
          budget: '', description: '', preferredDate: '', preferredTime: '',
        });
        onClose(false);
      } else {
        toast.error(data.message || 'Failed to submit booking. Please try again.');
      }
    } catch (error) {
      console.error('Booking error:', error);
      toast.error('Failed to submit booking. Please call us at (234) 205-1010');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg w-[95vw] max-h-[90vh] overflow-y-auto p-0 bg-slate-900 border-slate-700">
        <DialogHeader className="sticky top-0 z-10 px-6 py-4 bg-slate-800 border-b border-slate-700">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2 text-white text-xl font-semibold">
              <CalendarCheck className="h-6 w-6 text-orange-400" />
              Book Your Service
            </DialogTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onClose(false)}
              className="text-slate-400 hover:text-white hover:bg-slate-700"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Contact Information - Always Visible */}
          <div className="space-y-4 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
            <h3 className="flex items-center gap-2 text-white font-semibold">
              <User className="h-4 w-4 text-orange-400" />
              Contact Information
            </h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="name" className="text-slate-300 text-sm">Full Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="John Doe"
                  className="mt-1 bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500"
                  data-testid="booking-name-input"
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-slate-300 text-sm">Phone Number *</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="(216) 555-0123"
                  className="mt-1 bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500"
                  data-testid="booking-phone-input"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="email" className="text-slate-300 text-sm">Email Address *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="john@example.com"
                className="mt-1 bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500"
                data-testid="booking-email-input"
              />
            </div>

            <div>
              <Label htmlFor="address" className="text-slate-300 text-sm flex items-center gap-1">
                <MapPin className="h-3 w-3 text-orange-400" />
                Service Address
              </Label>
              <Input
                ref={addressInputRef}
                id="address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="Start typing to search..."
                className="mt-1 bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500"
                data-testid="booking-address-input"
                autoComplete="off"
              />
              <p className="text-xs text-slate-500 mt-1">Address autocomplete powered by Google</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="city" className="text-slate-300 text-sm">City</Label>
                <Input
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="Cleveland"
                  className="mt-1 bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500"
                />
              </div>
              <div>
                <Label htmlFor="zip" className="text-slate-300 text-sm">ZIP Code</Label>
                <Input
                  id="zip"
                  name="zip"
                  value={formData.zip}
                  onChange={handleInputChange}
                  placeholder="44101"
                  className="mt-1 bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500"
                />
              </div>
            </div>
          </div>

          {/* Accordion Sections */}
          <Accordion type="multiple" defaultValue={["service"]} className="space-y-2">
            {/* Service Details */}
            <AccordionItem value="service" className="border border-slate-700 rounded-lg bg-slate-800/50 overflow-hidden">
              <AccordionTrigger className="px-4 py-3 text-white hover:no-underline hover:bg-slate-800">
                <span className="flex items-center gap-2">
                  <Wrench className="h-4 w-4 text-orange-400" />
                  Service Details *
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4 space-y-3">
                <div>
                  <Label className="text-slate-300 text-sm">Service Type *</Label>
                  <Select value={formData.serviceType} onValueChange={(v) => handleSelectChange('serviceType', v)}>
                    <SelectTrigger className="mt-1 bg-slate-900/50 border-slate-600 text-white" data-testid="booking-service-select">
                      <SelectValue placeholder="Select service type" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-600">
                      {serviceTypes.map(s => (
                        <SelectItem key={s.value} value={s.value} className="text-white hover:bg-slate-700">{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-slate-300 text-sm">Urgency</Label>
                  <Select value={formData.urgency} onValueChange={(v) => handleSelectChange('urgency', v)}>
                    <SelectTrigger className="mt-1 bg-slate-900/50 border-slate-600 text-white">
                      <SelectValue placeholder="How soon do you need service?" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-600">
                      {urgencyLevels.map(u => (
                        <SelectItem key={u.value} value={u.value} className="text-white hover:bg-slate-700">{u.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Property Details */}
            <AccordionItem value="property" className="border border-slate-700 rounded-lg bg-slate-800/50 overflow-hidden">
              <AccordionTrigger className="px-4 py-3 text-white hover:no-underline hover:bg-slate-800">
                <span className="flex items-center gap-2">
                  <Home className="h-4 w-4 text-orange-400" />
                  Property Details
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4 space-y-3">
                <div>
                  <Label className="text-slate-300 text-sm">Property Type</Label>
                  <Select value={formData.propertyType} onValueChange={(v) => handleSelectChange('propertyType', v)}>
                    <SelectTrigger className="mt-1 bg-slate-900/50 border-slate-600 text-white">
                      <SelectValue placeholder="Select property type" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-600">
                      {propertyTypes.map(p => (
                        <SelectItem key={p.value} value={p.value} className="text-white hover:bg-slate-700">{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-slate-300 text-sm">Budget Range</Label>
                  <Select value={formData.budget} onValueChange={(v) => handleSelectChange('budget', v)}>
                    <SelectTrigger className="mt-1 bg-slate-900/50 border-slate-600 text-white">
                      <SelectValue placeholder="Select budget range" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-600">
                      {budgetRanges.map(b => (
                        <SelectItem key={b.value} value={b.value} className="text-white hover:bg-slate-700">{b.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Project Description */}
            <AccordionItem value="description" className="border border-slate-700 rounded-lg bg-slate-800/50 overflow-hidden">
              <AccordionTrigger className="px-4 py-3 text-white hover:no-underline hover:bg-slate-800">
                <span className="flex items-center gap-2">
                  <Camera className="h-4 w-4 text-orange-400" />
                  Project Description
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                <div>
                  <Label className="text-slate-300 text-sm">Describe the issue or project</Label>
                  <Textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Please describe what you need help with..."
                    className="mt-1 min-h-[100px] bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500"
                    data-testid="booking-description-input"
                  />
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Scheduling */}
            <AccordionItem value="scheduling" className="border border-slate-700 rounded-lg bg-slate-800/50 overflow-hidden">
              <AccordionTrigger className="px-4 py-3 text-white hover:no-underline hover:bg-slate-800">
                <span className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-orange-400" />
                  Preferred Schedule
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4 space-y-3">
                <div>
                  <Label className="text-slate-300 text-sm">Preferred Date</Label>
                  <Input
                    type="date"
                    name="preferredDate"
                    value={formData.preferredDate}
                    onChange={handleInputChange}
                    className="mt-1 bg-slate-900/50 border-slate-600 text-white"
                  />
                </div>
                
                <div>
                  <Label className="text-slate-300 text-sm">Preferred Time</Label>
                  <Select value={formData.preferredTime} onValueChange={(v) => handleSelectChange('preferredTime', v)}>
                    <SelectTrigger className="mt-1 bg-slate-900/50 border-slate-600 text-white">
                      <SelectValue placeholder="Select time preference" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-600">
                      {timePreferences.map(t => (
                        <SelectItem key={t.value} value={t.value} className="text-white hover:bg-slate-700">{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full h-12 btn-gradient-orange text-white font-semibold rounded-lg shadow-lg disabled:opacity-50"
            data-testid="booking-submit-button"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <CheckCircle className="h-5 w-5 mr-2" />
                Submit Booking Request
              </>
            )}
          </Button>

          <p className="text-center text-xs text-slate-500">
            We'll contact you within 1 hour to confirm your appointment
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};
